import React from 'react';
import { SHAPES, type Shape, type ShootDay } from '../../utils/clientSetup';
import { LENGTHS, type ExtraDetail, type ExtraSpec } from '../../utils/clientExtras';
import { SetupCount, SetupSwitch } from './SetupChoice';
import { SetupDate, SetupDays } from './SetupDays';

/**
 * The questions one chosen extra asks — and only the ones that apply to it.
 *
 * This is the whole point of the 16 Oct 2026 rebuild. Before it, every
 * extra got the same three questions a film gets, so a live stream was
 * asked what shape it was and a title pack was asked how many landscape
 * versions of itself there were. Tom: *"the options don't correspond to
 * what we might put in here."*
 *
 * The spec in utils/clientExtras.ts decides what is drawn; this file just
 * obeys it. Adding a type later means adding a row to EXTRAS, not editing
 * this component — unless the new type genuinely asks something none of
 * the others do, which is the bar for touching it.
 *
 * THE LIVE STREAM IS THE ODD ONE AND IS MEANT TO BE. It has no count and
 * no shape, because it is days rather than files. Its days are ordinary
 * shoot days — same control, same fields — so the client's schedule and
 * the call sheets get them for free. The only thing it asks beyond the
 * days is whether a recording is handed over at the end, and that is the
 * question that decides whether it puts anything on the page at all.
 */
const BIG = 'mt-4 w-full border-0 border-b border-paper/25 bg-transparent pb-3 font-display text-3xl font-light leading-none text-paper focus:border-paper focus:outline-none sm:text-4xl';
export function SetupExtraFields({
  spec,
  detail,
  onChange
}: {
  spec: ExtraSpec;
  detail: ExtraDetail;
  onChange: (patch: Partial<ExtraDetail>) => void;
}) {
  /* A live stream is a run of days, not a pile of files. */
  if (spec.asks.stream) {
    return <>
        <SetupDays days={detail.days} onDays={(days: ShootDay[]) => onChange({
        days
      })} data-id="element-1320" data-mp="edwthqfogzu2" />

        <SetupSwitch question="Is there a recording to deliver?" options={[{
        key: 'yes',
        label: 'Yes'
      }, {
        key: 'no',
        label: 'No'
      }]} value={detail.recording ? 'yes' : 'no'} onPick={key => onChange({
        recording: key === 'yes'
      })} data-id="element-1321" data-mp="9f3c3mx9soom" />

        {detail.recording && <SetupDate label="When do they need the recordings" value={detail.due} onChange={due => onChange({
        due
      })} data-id="element-1322" data-mp="vdpropkb3gxl" />}
      </>;
  }

  /* A title pack is one thing however many graphics are in it, so the
     count only appears once it is separate pieces. */
  const counted = !(spec.asks.pack && detail.pack);
  return <>
      {/* Without this the pieces arrive called Piece 1, Piece 2. */}
      {spec.asks.name && <label className="mb-14 block" data-id="element-1323" data-mp="3b5dpsob23fhz">
          <span className="label block text-paper/40" data-id="element-1324" data-mp="3b4c29kcut0jd">What is it</span>
          <input value={detail.named} autoFocus className={BIG} onChange={event => onChange({
        named: event.target.value
      })} data-id="element-1325" data-mp="3g2ncvxaz3f2n" />
          <span className="mt-3 block text-[0.9rem] font-light text-paper/35" data-id="element-1326" data-mp="1t6ph01skngv2">
            Whatever you call it — it names them on their page.
          </span>
        </label>}

      {spec.asks.pack && <SetupSwitch question="How does it arrive" options={[{
      key: 'pack',
      label: 'One pack'
    }, {
      key: 'pieces',
      label: 'Separate pieces'
    }]} value={detail.pack ? 'pack' : 'pieces'} onPick={key => onChange({
      pack: key === 'pack'
    })} data-id="element-1327" data-mp="1j7q7kgzzfh70" />}

      {counted && <SetupCount value={detail.count} ongoing={detail.ongoing} pieces={spec.pieces} onCount={count => onChange({
      count
    })} onOngoing={ongoing => onChange({
      ongoing
    })} data-id="element-1328" data-mp="15f0mcesa2gay" />}

      {spec.asks.length && <SetupSwitch question="How long" options={LENGTHS} value={detail.length} onPick={length => onChange({
      length
    })} data-id="element-1329" data-mp="qkvz6igfw89w" />}

      {spec.shaped && <SetupSwitch question="What shape" options={SHAPES} value={detail.shape} onPick={key => onChange({
      shape: key as Shape
    })} data-id="element-1330" data-mp="2vnj88yn00aeq" />}

      <SetupDate label="When do they need it" value={detail.due} onChange={due => onChange({
      due
    })} data-id="element-1331" data-mp="2ltu2c4ykbu0x" />
    </>;
}